import { Alert, AlertColor, Box, Button, Grid, Snackbar, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import BackIcon from '../../../icons/backIcon.svg';
import { announcementStyles } from './CustomStyles';
import { Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { AnnouncementDetail } from './AnnouncementDetail';
import { AnnouncementMessage } from './AnnouncementMessage';
import { AnnouncementSendTo } from './AnnouncementSendTo';
import { AnnouncementSchedule } from './AnnouncementSchedule';
import { AnnouncementReview } from './AnnouncementReview';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAnnouncement, resetDeleteAnnouncementState, resetPublishAnnouncement, selectAnnouncementsState, setPublishAnnouncement, updateAnnouncement } from '../sagas/announcementSlice';
import DeleteIcon from '../../../icons/delete_Icon.svg';
import { ConfirmAnnouncementDeleteModal } from './ConfirmAnnouncementDeleteModal';
import BadRequestIcon from '../../../icons/Badrequesterror.svg';
import Error500 from '../../../icons/505Error.svg';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { AnnouncementTemplateDto, SearchSenderIdentityDto, SenderIdentityStatus, UpdateAnnouncementDto, UpdateAnnouncementDtoStatusEnum } from '@inosio/payments-sdk';
import { LoadingButton } from '@mui/lab';
import { filterDistinctPersonalizations } from './utils/utils';
import {
  AnnouncementBodyValidator,
  AnnouncementDetailValidator,
  AnnouncementScheduleValidator,
  AnnouncementSendToValidator,
  AnnouncementReviewValidator,
  BaseAnnouncementItemValidator,
} from '../validators/AnnouncementValidators';
import { getSenderIdentities, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import debounce from 'lodash.debounce';

export interface AnnouncementWizardProps {}

//the order here matters
export enum WizardPageStates {
  Details = 'details',
  Message = 'message',
  SendTo = 'sendto',
  Schedule = 'schedule',
  Review = 'review',
}

export const AnnouncementWizard = (props: AnnouncementWizardProps) => {
  const [pendingAnnouncement, setPendingAnnouncement] = useState<Partial<UpdateAnnouncementDto> | null>(null);
  const { uuid: announcementUuid } = useParams<{ uuid: string }>();
  const [openConfirmAnnouncementDeleteModal, setOpenConfirmAnnouncementDeleteModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const {
    announcementDetail,
    isGettingAnnouncement,
    isUpdatingAnnouncementFailed,
    isDeletingAnnouncementFailed,
    isDeletingAnnouncementSuccess,
    AnnouncementDetailError,
    UpdateAnnouncementError,
    DeleteAnnouncementError,
    PublishingAnnouncementDetailError,
    isPublishingAnnouncement,
    isPublishingAnnouncementFailed,
    isPublishingAnnouncementSuccess,
  } = useSelector(selectAnnouncementsState);

  const { propertyGroupDetails, propertyGroups } = useSelector(selectPropertyGroupDetailsState);
  const [snackBar, setSnackBar] = useState<{ severity: AlertColor; message?: string } | null>(null);
  const [showScheduleButton, setShowScheduleButton] = useState<boolean>(false);
  const { pathname } = useLocation();
  let { url } = useRouteMatch();
  const history = useHistory();

  const classes = announcementStyles();

  const announcementDetailValidator = new AnnouncementDetailValidator(propertyGroupDetails.CommunicationsSettings?.CommunicationConfig!, announcementDetail);
  const announcementBodyValidator = new AnnouncementBodyValidator(propertyGroupDetails.CommunicationsSettings?.CommunicationConfig!, announcementDetail);
  const announcementSendToValidator = new AnnouncementSendToValidator(propertyGroupDetails.CommunicationsSettings?.CommunicationConfig!, announcementDetail);
  const announcementScheduleValidator = new AnnouncementScheduleValidator(propertyGroupDetails.CommunicationsSettings?.CommunicationConfig!, announcementDetail);
  const announcementReviewValidator = new AnnouncementReviewValidator(
    propertyGroupDetails.CommunicationsSettings?.CommunicationConfig!,
    announcementDetail,
    announcementDetailValidator,
    announcementBodyValidator,
    announcementSendToValidator,
    announcementScheduleValidator,
  );

  const isAnnouncementValid = announcementReviewValidator.IsEntireObjectComplete(announcementDetail);

  /**
   * Computes the current tab the user is on
   * @returns
   */
  const computeCurrentTab = () => {
    const parts = pathname.split('/').filter(Boolean);
    const lastPartOfPath = parts[parts.length - 1];
    const secondLastPartOfPath = parts[parts.length - 2];
    if (Object.values(WizardPageStates).includes(lastPartOfPath as WizardPageStates)) {
      return lastPartOfPath as WizardPageStates;
    } else if (parts.length > 1 && Object.values(WizardPageStates).includes(secondLastPartOfPath as WizardPageStates)) {
      return parts[parts.length - 2] as WizardPageStates;
    }

    return WizardPageStates.Details;
  };
  const myCurrenTab = computeCurrentTab();

  const handleSubmit = () => {};

  const handleTabChange = (event: React.SyntheticEvent, newValue: WizardPageStates) => {
    history.push(`${url}/${newValue.toLowerCase()}`);
  };

  const handleGoPreviousTab = () => {
    // If the current tab does not have a parameter, navigate back using the existing logic
    const currentTabIndex = Object.values(WizardPageStates).indexOf(myCurrenTab);
    if (currentTabIndex === 0) {
      history.replace('/pmui/announcements/scheduled');
    } else {
      let newTabIndex = currentTabIndex - 1;
      let newTabValue = Object.values(WizardPageStates)[newTabIndex];
      history.push(`${url}/${newTabValue.toLowerCase()}`);
    }
  };

  const handleGoNextTab = () => {
    if (myCurrenTab === WizardPageStates.Review) {
    } else {
      // Calculate the index of the current tab
      const currentTabIndex = Object.values(WizardPageStates).indexOf(myCurrenTab);
      let newTabIndex = currentTabIndex + 1;
      let newTabValue = Object.values(WizardPageStates)[newTabIndex];

      history.push(`${url}/${newTabValue.toLowerCase()}`);
    }
  };

  const tabSx = {
    textTransform: 'capitalize',
    '&.Mui-selected': {
      color: '#000 !important',
      border: 'none !important',
    },
  };

  const tabHorizontalLine = {
    width: '100%',
    height: '2px',
    backgroundColor: '#ccc',
    marginTop: '12px',
    marginBottom: '12px',
  };

  const handleSchedule = () => {
    const updateAnnouncementDto: UpdateAnnouncementDto = {
      Status: isAnnouncementValid ? UpdateAnnouncementDtoStatusEnum.Scheduled : UpdateAnnouncementDtoStatusEnum.Draft,
      Personalizations: filterDistinctPersonalizations(announcementDetail.Subject, announcementDetail.Body, announcementDetail.Personalizations),
    };

    dispatch(
      setPublishAnnouncement({
        uuid: announcementUuid,
        updateAnnouncementDto: updateAnnouncementDto,
      }),
    );
  };

  useEffect(() => {
    setShowScheduleButton(myCurrenTab === WizardPageStates.Review);
  }, [myCurrenTab]);

  const handleDeleteAnnouncementModal = () => {
    setOpenConfirmAnnouncementDeleteModal(true);
  };

  useEffect(() => {
    fetchAnnouncementDetail();
  }, [dispatch, announcementUuid]);

  const fetchAnnouncementDetail = () => {
    dispatch(
      getAnnouncement({
        uuid: announcementUuid,
      }),
    );
  };

  const getErrorIcon = (statusCode: number | undefined) => {
    if (String(statusCode)[0] === '4') {
      return BadRequestIcon;
    } else {
      return Error500;
    }
  };

  useEffect(() => {
    if (isUpdatingAnnouncementFailed && UpdateAnnouncementError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: UpdateAnnouncementError.message,
      });
    }
  }, [UpdateAnnouncementError, isUpdatingAnnouncementFailed]);

  useEffect(() => {
    if (isPublishingAnnouncementFailed && PublishingAnnouncementDetailError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: PublishingAnnouncementDetailError.message,
      });
      dispatch(resetPublishAnnouncement());
    }
  }, [PublishingAnnouncementDetailError, isPublishingAnnouncementFailed]);

  useEffect(() => {
    if (isDeletingAnnouncementFailed && DeleteAnnouncementError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: DeleteAnnouncementError.message,
      });
    }
  }, [isDeletingAnnouncementFailed, DeleteAnnouncementError]);

  useEffect(() => {
    if (isDeletingAnnouncementSuccess) {
      setSnackBar({
        severity: 'success',
        message: 'Announcement deleted successfully.',
      });
      dispatch(resetDeleteAnnouncementState());
      history.replace('/pmui/announcements/scheduled');
    }
  }, [isDeletingAnnouncementSuccess]);

  useEffect(() => {
    if (isPublishingAnnouncementSuccess) {
      history.push('/pmui/announcements/scheduled');
      dispatch(resetPublishAnnouncement());
    }
  }, [isPublishingAnnouncementSuccess]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBar(null);
  };

  useEffect(() => {
    const searchSenderIdentityDto: SearchSenderIdentityDto = {
      Status: SenderIdentityStatus.Enabled,
    };

    const defaultPropertyGroup = propertyGroups.find((propertyGroup) => propertyGroup.PropertyGroupType === 'Default');

    if (defaultPropertyGroup) {
      dispatch(
        getSenderIdentities({
          propertyGroupUuid: defaultPropertyGroup?.Uuid,
          searchSenderIdentityDto: searchSenderIdentityDto,
        }),
      );
    }
  }, [dispatch]);

  const actuallyDispatchAnnouncementUpdate = () => {
    if (pendingAnnouncement) {
      dispatch({
        type: updateAnnouncement.type,
        payload: {
          uuid: announcementDetail.TemplateUuid,
          updateAnnouncementDto: {
            ...pendingAnnouncement,
          },
        },
      });
      setPendingAnnouncement(null);
    }
  };

  const delayedAnnouncementUpdate = useCallback(debounce(actuallyDispatchAnnouncementUpdate, 750), [pendingAnnouncement]);

  const onHandleSetPendingAnnouncement = useCallback(
    (changeAnnouncement) => {
      setPendingAnnouncement({
        ...pendingAnnouncement,
        ...changeAnnouncement,
      });
    },
    [pendingAnnouncement],
  );

  useEffect(() => {
    delayedAnnouncementUpdate();
    return delayedAnnouncementUpdate.cancel;
  }, [pendingAnnouncement, delayedAnnouncementUpdate]);

  const renderCompleteIcon = (
    <img
      style={{
        width: '12px',
        height: '9px',
        marginRight: '10px',
      }}
      src={CheckMarkIcon}
      alt="Completed"
    />
  );

  const renderInCompleteIcon = (
    <img
      style={{
        width: '12px',
        height: '16px',
        marginRight: '10px',
      }}
      src={IncompleteIcon}
      alt="Incomplete"
    />
  );

  const generateTabLabel = (label: string, tabKey: WizardPageStates, validator: BaseAnnouncementItemValidator<AnnouncementTemplateDto>) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {validator.IsEntireObjectComplete(announcementDetail) ? renderCompleteIcon : renderInCompleteIcon}
        <Typography sx={{ fontWeight: myCurrenTab === tabKey ? 'bold' : 400 }}>{label}</Typography>
      </Box>
    );
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ background: '#FFFFFF', borderRadius: '24px', position: 'relative', minHeight: 'calc( 100vh - 144px )' }}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={!!snackBar} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={snackBar?.severity}>
          {snackBar?.message}
        </Alert>
      </Snackbar>

      <Box pt={2}>
        {/* Header BOX */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, borderBottom: '1px solid #EEEEEE', pb: 2, height: '70px' }}>
          {/* Back button container */}
          <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={handleGoPreviousTab}>
            <img
              style={{
                width: '20px',
                height: '20px',
              }}
              src={BackIcon}
              alt="backicon"
            />
            <Typography sx={{ ml: 1, color: '#39A2DB' }} className={classes.labelText}>
              Back
            </Typography>
          </Box>

          {/* Tabs container with flex-grow to take remaining space */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Tabs
              value={myCurrenTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs"
              sx={{
                '& .MuiTabs-flexContainer': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab sx={tabSx} className={classes.wizardText} label={generateTabLabel('Details', WizardPageStates.Details, announcementDetailValidator)} value={WizardPageStates.Details} />
              <Box sx={tabHorizontalLine} />
              <Tab sx={tabSx} className={classes.wizardText} label={generateTabLabel('Message', WizardPageStates.Message, announcementBodyValidator)} value={WizardPageStates.Message} />
              <Box sx={tabHorizontalLine} />
              <Tab sx={tabSx} className={classes.wizardText} label={generateTabLabel('Send To', WizardPageStates.SendTo, announcementSendToValidator)} value={WizardPageStates.SendTo} />
              <Box sx={tabHorizontalLine} />
              <Tab sx={tabSx} className={classes.wizardText} label={generateTabLabel('Schedule', WizardPageStates.Schedule, announcementScheduleValidator)} value={WizardPageStates.Schedule} />
              <Box sx={tabHorizontalLine} />
              <Tab sx={tabSx} className={classes.wizardText} label={generateTabLabel('Review', WizardPageStates.Review, announcementReviewValidator)} value={WizardPageStates.Review} />
            </Tabs>
          </Box>

          {/* Action buttons container */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title="Delete announcement." placement="top">
              <img
                src={DeleteIcon}
                style={{
                  height: '20px',
                  width: '20px',
                  cursor: 'pointer',
                  marginRight: '15px',
                }}
                alt="RemoveAnnouncement"
                onClick={handleDeleteAnnouncementModal}
              />
            </Tooltip>

            {showScheduleButton ? (
              <Box>
                <Tooltip disableHoverListener={isAnnouncementValid} placement="bottom" title="Please complete all required fields to schedule this announcement.">
                  <LoadingButton
                    className={`${classes.wizardBtn} ${!isAnnouncementValid ? classes.wizardBtnDisabled : null}`}
                    loading={isPublishingAnnouncement}
                    //disabled={!isAnnouncementValid || isPublishingAnnouncement}
                    variant="accountManagement"
                    sx={{ ml: 2 }}
                    onClick={handleSchedule}
                  >
                    <Typography className={classes.wizardBtnText} sx={{ margin: '0 10px' }}>
                      Schedule
                    </Typography>
                  </LoadingButton>
                </Tooltip>
              </Box>
            ) : (
              <Button type="button" className={classes.wizardNextBtn} onClick={handleGoNextTab}>
                <Typography className={classes.wizardContinueBtnText} sx={{ margin: '0 10px' }}>
                  Continue
                </Typography>
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 2, pt: 1 }}>
          {AnnouncementDetailError?.statusCode ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={getErrorIcon(AnnouncementDetailError?.statusCode)} alt={String(AnnouncementDetailError?.statusCode)} style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {AnnouncementDetailError?.statusCode}
                  </Typography>
                </Grid>
                {AnnouncementDetailError?.statusCode === 404 && (
                  <Grid mt={2} item justifyContent="center">
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '23px',
                        color: '#9E9E9E',
                        textAlign: 'center',
                        marginTop: '10px',
                      }}
                    >
                      Page not found.
                    </Typography>
                  </Grid>
                )}
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {AnnouncementDetailError?.message?.length === 0 ? '' : AnnouncementDetailError?.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : isGettingAnnouncement ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: 'calc(100vh - 215px)',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <Switch>
              <Route path={`${url}/${WizardPageStates.Details.toLowerCase()}`}>
                <AnnouncementDetail setPendingAnnouncement={onHandleSetPendingAnnouncement} />
              </Route>
              <Route path={`${url}/${WizardPageStates.Message.toLowerCase()}`}>
                <AnnouncementMessage setPendingAnnouncement={onHandleSetPendingAnnouncement} />
              </Route>
              <Route path={`${url}/${WizardPageStates.SendTo.toLowerCase()}`}>
                <AnnouncementSendTo setPendingAnnouncement={onHandleSetPendingAnnouncement} />
              </Route>
              <Route path={`${url}/${WizardPageStates.Schedule.toLowerCase()}`}>
                <AnnouncementSchedule setPendingAnnouncement={onHandleSetPendingAnnouncement} />
              </Route>
              <Route path={`${url}/${WizardPageStates.Review.toLowerCase()}`}>
                <AnnouncementReview isComplete={isAnnouncementValid} />
              </Route>
            </Switch>
          )}
        </Box>
      </Box>

      <ConfirmAnnouncementDeleteModal announcementUuid={announcementUuid} open={openConfirmAnnouncementDeleteModal} onClose={() => setOpenConfirmAnnouncementDeleteModal(false)} />
    </Box>
  );
};
